/**
 * Browser shim for `process.env`.
 *
 * Why:
 * - This frontend codebase uses `process.env.*` to stay compatible with Jest (Node).
 * - In the browser, `process` is not defined, which can crash at runtime.
 *
 * Vite exposes environment variables via `import.meta.env`. This shim maps those
 * values onto `globalThis.process.env` when running in the browser.
 *
 * NOTE:
 * - This file intentionally uses `import.meta.env` and must NOT be imported by Jest tests.
 *   We only import it from the app entrypoint (`main.tsx`) which tests don't load.
 */
export function ensureBrowserProcessEnv(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = globalThis as any;

  if (!g.process) {
    g.process = { env: {} };
  }

  if (!g.process.env) {
    g.process.env = {};
  }

  // Merge Vite env into process.env (do not overwrite existing keys set by other tooling)
  try {
    // `import.meta.env` is provided by Vite in dev and production builds
    const viteEnv = import.meta.env as unknown as Record<string, unknown>;
    for (const [k, v] of Object.entries(viteEnv)) {
      if (typeof g.process.env[k] === "undefined") {
        g.process.env[k] = v;
      }
    }
  } catch {
    // If `import.meta.env` is unavailable for some reason, do nothing.
  }
}
